import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import _ from 'lodash';
import './RecipientCheck.scss';

import InfoTip from './InfoTip';
import LabeledSelect from './LabeledSelect';
import { checkNotificationForRecipient } from '../../../api/notifications';
import { humanizeName } from '../../../helpers/notification_helpers';

const GATE_TIPS = {
  switch: 'Their Settings → Notifications switch for this notification, read per channel. No settings row means every channel is off.',
  age_gate: 'Some click notifications are only for accounts under a certain age. Read from the account creation date.',
  daily_ceiling: "The click notifications share one seat a day per creator. Read from today's receipts on the primary, exactly as the worker reads them.",
  launch_mode: 'The lane\u2019s rollout row: shadow files a receipt and sends nothing, allowlist sends only to listed accounts, live sends to everyone outside the holdout.',
  moment: 'A click notification needs a real moment: which pin, how many clicks. Only the worker\u2019s sweep has that.',
  caps: 'The push caps for this notification\u2019s budget category, read with the same query the send uses, without reserving the seat.',
  throttle: 'The repeat window on this email: nothing goes out if the same email reached them inside it.',
  superseded_by_push: 'When the push is delivered this email is skipped. Only a real send knows whether the push lands.'
};

const STOP_PHRASES = {
  switch: 'their switch',
  age_gate: 'the age gate',
  daily_ceiling: 'the daily ceiling',
  launch_mode: 'launch mode',
  caps: 'the category caps',
  throttle: 'the repeat window'
};

const OPEN_PHRASES = {
  moment: 'The rest needs a real click moment.',
  superseded_by_push: 'Unless the push lands first.',
  switch: 'Their switch depends on the send\u2019s data.'
};

const MARKS = { pass: '\u2713', fail: '\u2715', not_checked: '\u2013', unknown: '?' };

// "If this notification fired for this person right now, would they get it?" The bar sits with the
// search; the server runs the real gates read-only and hands the verdict to onResult.
export const RecipientCheckBar = ({ user, templatesByName, onResult }) => {
  const [templateName, setTemplateName] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const requestVersion = useRef(0);

  useEffect(() => () => { requestVersion.current++; }, [user.id]);

  const names = _.sortBy(Object.keys(templatesByName), humanizeName);
  const options = [{ value: '', label: 'Choose a notification…' }, ...names.map(name => ({ value: name, label: humanizeName(name) }))];
  const firstName = (user.name || user.username || '').split(' ')[0] || 'this person';

  const check = () => {
    const version = ++requestVersion.current;
    const isCurrent = () => version === requestVersion.current;
    setIsChecking(true);
    checkNotificationForRecipient(user.id, templateName)
      .then(result => {
        if (isCurrent() && result.userId === user.id && result.templateName === templateName) onResult(result);
      })
      .catch(() => isCurrent() && window.ALERT?.error?.('Failed to check this notification.'))
      .finally(() => isCurrent() && setIsChecking(false));
  };

  const choose = name => {
    requestVersion.current++;
    setIsChecking(false);
    setTemplateName(name);
    onResult(null);
  };

  return (
    <div className='recipient-check-bar'>
      <LabeledSelect
        label='Check a notification'
        tip={`If a notification fired for ${firstName} right now, would they get it? Runs the real rules against real data. Sends nothing, writes nothing.`}
        value={templateName}
        onChange={choose}
        options={options}
      />
      <button type='button' className={cn('check-button', { disabled: !templateName || isChecking })} disabled={!templateName || isChecking} onClick={check}>
        {isChecking ? 'Checking…' : 'Check'}
      </button>
    </div>
  );
};

RecipientCheckBar.propTypes = {
  user: PropTypes.object.isRequired,
  templatesByName: PropTypes.object.isRequired,
  onResult: PropTypes.func.isRequired
};

// The verdict, full width above the log: one row per channel, the gates as the server walked them.
export const RecipientCheckResult = ({ result, onDismiss }) => (
  <div className='recipient-check-result'>
    <div className='result-head'>
      <span className='result-template'>{humanizeName(result.templateName)}</span>
      <span className='result-fine-print'>Real rules, real data. Sends nothing, writes nothing.</span>
      <span className='result-dismiss' title='Clear this check' onClick={onDismiss}>
        ✕
      </span>
    </div>
    {result.channels.length === 0 && <div className='result-verdict'>This notification has no channel to check.</div>}
    {result.channels.map(channel => (
      <div key={channel.channel} className='result-channel'>
        <div className='result-verdict'>
          <span className={cn('channel-badge', channel.channel)}>{channel.channel}</span>
          {verdictText(channel)}
        </div>
        <div className='gate-row'>
          {channel.gates.map(gate => (
            <div key={gate.key} className={cn('gate', gate.result)}>
              <span className='gate-mark'>{MARKS[gate.result]}</span>
              <span className='gate-body'>
                <span className='gate-label'>
                  {gate.label}
                  {GATE_TIPS[gate.key] && <InfoTip message={GATE_TIPS[gate.key]} />}
                </span>
                <span className='gate-detail'>{gate.detail}</span>
              </span>
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

const verdictText = channel => {
  if (channel.wouldSend === false) return `Would not send. Stops at ${STOP_PHRASES[channel.stoppedAt] || channel.stoppedAt}.`;
  if (channel.wouldSend === true) return 'Would send.';
  const open = channel.gates.find(gate => gate.result === 'unknown');
  return `Passes every rule we can check. ${OPEN_PHRASES[open?.key] || 'The rest only a real send knows.'}`;
};

RecipientCheckResult.propTypes = {
  result: PropTypes.object.isRequired,
  onDismiss: PropTypes.func.isRequired
};
